/**
 * Transport Management - Full Featured Page
 * Tabs: Dashboard, Vehicles, Routes, Drivers, Student Mapping, Fees
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = {
  get: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return await res.json();
    } catch (err) { return { error: err.message }; }
  },
  post: async (endpoint, data, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(data)
      });
      return await res.json();
    } catch (err) { return { error: err.message }; }
  },
  delete: async (endpoint, token) => {
    try {
      const res = await fetch(`${API_URL}${endpoint}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      return await res.json();
    } catch (err) { return { error: err.message }; }
  }
};

function StatCard({ label, value, sub, color = 'orange' }) {
  const colors = {
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    green: 'bg-green-50 border-green-200 text-green-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    red: 'bg-red-50 border-red-200 text-red-700',
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${colors[color]}`}>
      <div className="text-sm font-medium opacity-80">{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {sub && <div className="text-xs mt-1 opacity-70">{sub}</div>}
    </div>
  );
}

function Toast({ toast }) {
  if (!toast) return null;
  return (
    <div className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg text-white text-sm z-50 ${toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'}`}>
      {toast.message}
    </div>
  );
}

// ==================== DASHBOARD TAB ====================
function DashboardTab({ token, language }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await api.get('/api/transport/dashboard', token);
      if (res.success) setData(res);
      setLoading(false);
    })();
  }, [token]);

  if (loading) return <div className="p-8 text-center text-gray-500">{language === 'hi' ? 'लोड हो रहा है...' : 'Loading...'}</div>;
  if (!data) return <div className="p-8 text-center text-gray-500">{language === 'hi' ? 'कोई डेटा नहीं' : 'No data'}</div>;

  const s = data.summary;
  return (
    <div className="space-y-5" data-testid="transport-dashboard-tab">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard label={language === 'hi' ? 'कुल वाहन' : 'Total Vehicles'} value={s.total_vehicles} color="blue" />
        <StatCard label={language === 'hi' ? 'कुल रूट' : 'Total Routes'} value={s.total_routes} color="green" />
        <StatCard label={language === 'hi' ? 'कुल ड्राइवर' : 'Total Drivers'} value={s.total_drivers} color="purple" />
        <StatCard label={language === 'hi' ? 'छात्र (ट्रांसपोर्ट)' : 'Students'} value={s.total_students} color="orange" />
        <StatCard label={language === 'hi' ? 'कुल क्षमता' : 'Total Capacity'} value={s.total_capacity} color="slate" />
        <StatCard label={language === 'hi' ? 'उपयोग दर' : 'Utilization'} value={`${s.utilization_percentage}%`} color={s.utilization_percentage > 80 ? 'red' : 'green'} />
      </div>

      {/* Alerts */}
      {(data.alerts?.expiring_vehicle_docs?.length > 0 || data.alerts?.expiring_driver_licenses?.length > 0) && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <h3 className="font-semibold text-red-700 mb-2">{language === 'hi' ? 'अलर्ट - जल्द एक्सपायर' : 'Alerts - Expiring Soon'}</h3>
          {data.alerts.expiring_vehicle_docs.map((v, i) => (
            <div key={i} className="text-sm text-red-600">{v.vehicle_number} - RC/Insurance expiring</div>
          ))}
          {data.alerts.expiring_driver_licenses.map((d, i) => (
            <div key={i} className="text-sm text-red-600">{d.name} - License expiring {d.license_expiry}</div>
          ))}
        </div>
      )}

      {s.total_vehicles === 0 && s.total_routes === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border text-gray-400">
          <div className="text-5xl mb-3">🚌</div>
          <p className="text-lg">{language === 'hi' ? 'अभी कोई ट्रांसपोर्ट डेटा नहीं' : 'No transport data yet'}</p>
          <p className="text-sm mt-1">{language === 'hi' ? 'वाहन और रूट जोड़ना शुरू करें' : 'Start by adding vehicles and routes'}</p>
        </div>
      )}
    </div>
  );
}

// ==================== VEHICLES TAB ====================
function VehiclesTab({ token, language }) {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ vehicle_number: '', vehicle_type: 'bus', seating_capacity: 40 });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get('/api/transport/vehicles', token);
    setVehicles(res.vehicles || []);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.vehicle_number) return;
    setSaving(true);
    const res = await api.post('/api/transport/vehicles/simple', form, token);
    setSaving(false);
    if (res.success) {
      setShowAdd(false);
      setForm({ vehicle_number: '', vehicle_type: 'bus', seating_capacity: 40 });
      load();
      setToast({ message: language === 'hi' ? 'वाहन जोड़ा गया!' : 'Vehicle added!', type: 'success' });
    } else {
      setToast({ message: res.detail || res.error || 'Error', type: 'error' });
    }
    setTimeout(() => setToast(null), 3000);
  };

  const typeIcons = { bus: '🚌', van: '🚐', auto: '🛺', car: '🚗' };
  const statusColors = { active: 'bg-green-100 text-green-700', maintenance: 'bg-yellow-100 text-yellow-700', inactive: 'bg-red-100 text-red-700' };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="vehicles-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{vehicles.length} {language === 'hi' ? 'वाहन' : 'vehicles'}</p>
        <button data-testid="add-vehicle-btn" onClick={() => setShowAdd(!showAdd)}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition">
          {showAdd ? '✕' : '+'} {language === 'hi' ? 'वाहन जोड़ें' : 'Add Vehicle'}
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="add-vehicle-form">
          <h3 className="font-semibold text-gray-800">{language === 'hi' ? 'नया वाहन' : 'New Vehicle'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input data-testid="vehicle-number-input" placeholder={language === 'hi' ? 'वाहन नंबर (जैसे UP32AB1234) *' : 'Vehicle Number *'}
              value={form.vehicle_number} onChange={(e) => setForm({ ...form, vehicle_number: e.target.value.toUpperCase() })}
              className="px-3 py-2 border rounded-lg text-sm" />
            <select data-testid="vehicle-type-select" value={form.vehicle_type} onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="bus">{language === 'hi' ? 'बस' : 'Bus'}</option>
              <option value="van">{language === 'hi' ? 'वैन' : 'Van'}</option>
              <option value="auto">{language === 'hi' ? 'ऑटो' : 'Auto'}</option>
              <option value="car">{language === 'hi' ? 'कार' : 'Car'}</option>
            </select>
            <input data-testid="vehicle-capacity-input" type="number" placeholder={language === 'hi' ? 'सीटें' : 'Seats'}
              value={form.seating_capacity} onChange={(e) => setForm({ ...form, seating_capacity: parseInt(e.target.value) || 40 })}
              className="px-3 py-2 border rounded-lg text-sm" />
          </div>
          <button data-testid="save-vehicle-btn" onClick={handleAdd} disabled={saving || !form.vehicle_number}
            className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 transition">
            {saving ? '...' : (language === 'hi' ? 'वाहन जोड़ें' : 'Add Vehicle')}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {vehicles.map(v => (
          <div key={v.vehicle_id} className="bg-white rounded-xl border p-4 hover:shadow-md transition" data-testid={`vehicle-${v.vehicle_id}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="text-3xl">{typeIcons[v.vehicle_type] || '🚌'}</div>
              <div className="flex-1">
                <div className="font-bold text-gray-800">{v.vehicle_number}</div>
                <div className="text-xs text-gray-500 capitalize">{v.vehicle_type}</div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${statusColors[v.status] || 'bg-gray-100 text-gray-600'}`}>
                {v.status}
              </span>
            </div>
            <div className="flex gap-4 text-xs text-gray-500">
              <span>{language === 'hi' ? `${v.capacity || v.seating_capacity || 0} सीटें` : `${v.capacity || v.seating_capacity || 0} seats`}</span>
              <span>{language === 'hi' ? `${v.students_count || 0} छात्र` : `${v.students_count || 0} students`}</span>
            </div>
          </div>
        ))}
      </div>

      {vehicles.length === 0 && !showAdd && (
        <div className="text-center py-12 text-gray-400 bg-white rounded-xl border">
          <div className="text-5xl mb-3">🚌</div>
          <p>{language === 'hi' ? 'कोई वाहन नहीं जोड़ा' : 'No vehicles added yet'}</p>
        </div>
      )}
      <Toast toast={toast} />
    </div>
  );
}

// ==================== ROUTES TAB ====================
function RoutesTab({ token, language }) {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ route_name: '', start_point: '', end_point: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get('/api/transport/routes', token);
    setRoutes(res.routes || []);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.route_name) return;
    setSaving(true);
    const res = await api.post('/api/transport/routes/simple', form, token);
    setSaving(false);
    if (res.success) {
      setShowAdd(false);
      setForm({ route_name: '', start_point: '', end_point: '' });
      load();
      setToast({ message: language === 'hi' ? 'रूट जोड़ा गया!' : 'Route added!', type: 'success' });
    } else {
      setToast({ message: res.detail || 'Error', type: 'error' });
    }
    setTimeout(() => setToast(null), 3000);
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="routes-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{routes.length} {language === 'hi' ? 'रूट' : 'routes'}</p>
        <button data-testid="add-route-btn" onClick={() => setShowAdd(!showAdd)}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition">
          {showAdd ? '✕' : '+'} {language === 'hi' ? 'रूट जोड़ें' : 'Add Route'}
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="add-route-form">
          <h3 className="font-semibold text-gray-800">{language === 'hi' ? 'नया रूट' : 'New Route'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input data-testid="route-name-input" placeholder={language === 'hi' ? 'रूट का नाम *' : 'Route Name *'}
              value={form.route_name} onChange={(e) => setForm({ ...form, route_name: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder={language === 'hi' ? 'शुरू (जैसे रेलवे स्टेशन)' : 'Start Point'}
              value={form.start_point} onChange={(e) => setForm({ ...form, start_point: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder={language === 'hi' ? 'अंत (जैसे स्कूल)' : 'End Point'}
              value={form.end_point} onChange={(e) => setForm({ ...form, end_point: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm" />
          </div>
          <button data-testid="save-route-btn" onClick={handleAdd} disabled={saving || !form.route_name}
            className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 transition">
            {saving ? '...' : (language === 'hi' ? 'रूट जोड़ें' : 'Add Route')}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {routes.map(r => (
          <div key={r.route_id} className="bg-white rounded-xl border p-4 hover:shadow-md transition" data-testid={`route-${r.route_id}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center text-green-600 font-bold text-lg">
                {r.route_name?.charAt(0) || 'R'}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800">{r.route_name}</div>
                <div className="text-xs text-gray-500">
                  {r.start_point && r.end_point ? `${r.start_point} → ${r.end_point}` : language === 'hi' ? 'रूट' : 'Route'}
                </div>
              </div>
            </div>
            <div className="flex gap-4 text-xs text-gray-500 mt-2">
              <span>{r.stops?.length || 0} {language === 'hi' ? 'स्टॉप' : 'stops'}</span>
              <span>{r.students_count || 0} {language === 'hi' ? 'छात्र' : 'students'}</span>
              {r.distance && <span>{r.distance} km</span>}
            </div>
          </div>
        ))}
      </div>

      {routes.length === 0 && !showAdd && (
        <div className="text-center py-12 text-gray-400 bg-white rounded-xl border">
          <div className="text-5xl mb-3">🗺️</div>
          <p>{language === 'hi' ? 'कोई रूट नहीं जोड़ा' : 'No routes added yet'}</p>
        </div>
      )}
      <Toast toast={toast} />
    </div>
  );
}

// ==================== DRIVERS TAB ====================
function DriversTab({ token, language }) {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', license_number: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    const res = await api.get('/api/transport/drivers', token);
    setDrivers(res.drivers || []);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async () => {
    if (!form.name || !form.phone) return;
    setSaving(true);
    const res = await api.post('/api/transport/drivers/simple', form, token);
    setSaving(false);
    if (res.success) {
      setShowAdd(false);
      setForm({ name: '', phone: '', license_number: '' });
      load();
      setToast({ message: language === 'hi' ? 'ड्राइवर जोड़ा गया!' : 'Driver added!', type: 'success' });
    } else {
      setToast({ message: res.detail || 'Error', type: 'error' });
    }
    setTimeout(() => setToast(null), 3000);
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="drivers-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{drivers.length} {language === 'hi' ? 'ड्राइवर' : 'drivers'}</p>
        <button data-testid="add-driver-btn" onClick={() => setShowAdd(!showAdd)}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition">
          {showAdd ? '✕' : '+'} {language === 'hi' ? 'ड्राइवर जोड़ें' : 'Add Driver'}
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="add-driver-form">
          <h3 className="font-semibold text-gray-800">{language === 'hi' ? 'नया ड्राइवर' : 'New Driver'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input data-testid="driver-name-input" placeholder={language === 'hi' ? 'नाम *' : 'Name *'}
              value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm" />
            <input data-testid="driver-phone-input" placeholder={language === 'hi' ? 'फ़ोन नंबर *' : 'Phone *'}
              value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm" />
            <input placeholder={language === 'hi' ? 'लाइसेंस नंबर' : 'License Number'}
              value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm" />
          </div>
          <button data-testid="save-driver-btn" onClick={handleAdd} disabled={saving || !form.name || !form.phone}
            className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 transition">
            {saving ? '...' : (language === 'hi' ? 'ड्राइवर जोड़ें' : 'Add Driver')}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {drivers.map(d => (
          <div key={d.driver_id} className="bg-white rounded-xl border p-4 hover:shadow-md transition" data-testid={`driver-${d.driver_id}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center font-bold text-sm">
                {d.name?.charAt(0)?.toUpperCase() || 'D'}
              </div>
              <div className="flex-1">
                <div className="font-semibold text-gray-800">{d.name}</div>
                <div className="text-xs text-gray-500">{d.phone}</div>
              </div>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold capitalize ${d.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                {d.status}
              </span>
            </div>
            {d.license_number && (
              <div className="mt-2 text-xs text-gray-500">{language === 'hi' ? 'लाइसेंस' : 'License'}: {d.license_number}</div>
            )}
          </div>
        ))}
      </div>

      {drivers.length === 0 && !showAdd && (
        <div className="text-center py-12 text-gray-400 bg-white rounded-xl border">
          <div className="text-5xl mb-3">👨‍✈️</div>
          <p>{language === 'hi' ? 'कोई ड्राइवर नहीं जोड़ा' : 'No drivers added yet'}</p>
        </div>
      )}
      <Toast toast={toast} />
    </div>
  );
}

// ==================== STUDENT MAPPING TAB ====================
function StudentMappingTab({ token, language }) {
  const [assignments, setAssignments] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ student_id: '', route_id: '', stop_name: '', pickup_time: '07:30', drop_time: '14:30' });
  const [selectedClass, setSelectedClass] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    const [aRes, rRes, cRes] = await Promise.all([
      api.get('/api/transport/student-assignments', token),
      api.get('/api/transport/routes', token),
      api.get('/api/classes/', token),
    ]);
    setAssignments(aRes.assignments || []);
    setRoutes(rRes.routes || []);
    setClasses(cRes.classes || []);
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (selectedClass) {
      (async () => {
        const res = await api.get(`/api/students/?class_id=${selectedClass}`, token);
        setStudents(res.students || []);
      })();
    }
  }, [selectedClass, token]);

  const handleAssign = async () => {
    if (!form.student_id || !form.route_id) return;
    setSaving(true);
    const res = await api.post('/api/transport/student-assign', form, token);
    setSaving(false);
    if (res.success) {
      setShowAdd(false);
      setForm({ student_id: '', route_id: '', stop_name: '', pickup_time: '07:30', drop_time: '14:30' });
      load();
      setToast({ message: res.message || (language === 'hi' ? 'छात्र assign हुआ!' : 'Student assigned!'), type: 'success' });
    } else {
      setToast({ message: res.detail || 'Error', type: 'error' });
    }
    setTimeout(() => setToast(null), 3000);
  };

  const handleRemove = async (id) => {
    const res = await api.delete(`/api/transport/student-assign/${id}`, token);
    if (res.success) { load(); setToast({ message: language === 'hi' ? 'हटा दिया!' : 'Removed!', type: 'success' }); }
    setTimeout(() => setToast(null), 3000);
  };

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="student-mapping-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-gray-500">{assignments.length} {language === 'hi' ? 'छात्र assign' : 'students assigned'}</p>
        <button data-testid="assign-student-btn" onClick={() => setShowAdd(!showAdd)}
          className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition">
          {showAdd ? '✕' : '+'} {language === 'hi' ? 'छात्र Assign करें' : 'Assign Student'}
        </button>
      </div>

      {showAdd && (
        <div className="bg-white rounded-xl border p-5 space-y-3" data-testid="assign-form">
          <h3 className="font-semibold text-gray-800">{language === 'hi' ? 'छात्र को रूट पर Assign करें' : 'Assign Student to Route'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <select value={selectedClass} onChange={(e) => setSelectedClass(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="">{language === 'hi' ? 'कक्षा चुनें' : 'Select Class'}</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select data-testid="assign-student-select" value={form.student_id} onChange={(e) => setForm({ ...form, student_id: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm bg-white" disabled={!selectedClass}>
              <option value="">{language === 'hi' ? 'छात्र चुनें *' : 'Select Student *'}</option>
              {students.map(s => <option key={s.id} value={s.id}>{s.name} ({s.admission_number || '-'})</option>)}
            </select>
            <select data-testid="assign-route-select" value={form.route_id} onChange={(e) => setForm({ ...form, route_id: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm bg-white">
              <option value="">{language === 'hi' ? 'रूट चुनें *' : 'Select Route *'}</option>
              {routes.map(r => <option key={r.route_id} value={r.route_id}>{r.route_name}</option>)}
            </select>
            <input placeholder={language === 'hi' ? 'स्टॉप का नाम' : 'Stop Name'}
              value={form.stop_name} onChange={(e) => setForm({ ...form, stop_name: e.target.value })}
              className="px-3 py-2 border rounded-lg text-sm" />
          </div>
          <button data-testid="save-assign-btn" onClick={handleAssign} disabled={saving || !form.student_id || !form.route_id}
            className="w-full py-2.5 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 transition">
            {saving ? '...' : (language === 'hi' ? 'Assign करें' : 'Assign')}
          </button>
        </div>
      )}

      {/* Assignments Table */}
      {assignments.length > 0 && (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="assignments-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'छात्र' : 'Student'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'रूट' : 'Route'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'स्टॉप' : 'Stop'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">{language === 'hi' ? 'स्थिति' : 'Status'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500"></th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {assignments.map(a => (
                  <tr key={a.assignment_id} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-800">{a.student_name || a.student_id}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">{a.route_name || a.route_id}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-500">{a.stop_name || '-'}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${a.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {a.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <button data-testid={`remove-${a.assignment_id}`} onClick={() => handleRemove(a.assignment_id)}
                        className="text-red-400 hover:text-red-600 text-xs">
                        {language === 'hi' ? 'हटाएं' : 'Remove'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {assignments.length === 0 && !showAdd && (
        <div className="text-center py-12 text-gray-400 bg-white rounded-xl border">
          <div className="text-5xl mb-3">👨‍🎓</div>
          <p>{language === 'hi' ? 'कोई छात्र assign नहीं' : 'No students assigned to transport'}</p>
        </div>
      )}
      <Toast toast={toast} />
    </div>
  );
}

// ==================== FEES TAB ====================
function FeesTab({ token, language }) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const res = await api.get('/api/transport/fees/pending', token);
      setPending(res.pending || []);
      setLoading(false);
    })();
  }, [token]);

  if (loading) return <div className="p-8 text-center text-gray-500">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="fees-tab">
      {pending.length > 0 ? (
        <div className="bg-white rounded-xl border overflow-hidden">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="font-semibold text-gray-800">{language === 'hi' ? 'लंबित ट्रांसपोर्ट फीस' : 'Pending Transport Fees'}</h3>
            <p className="text-xs text-gray-500 mt-1">{pending.length} {language === 'hi' ? 'छात्रों की फीस लंबित' : 'students with pending fees'}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="pending-fees-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'छात्र' : 'Student'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">{language === 'hi' ? 'रूट' : 'Route'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">{language === 'hi' ? 'राशि' : 'Amount'}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500">{language === 'hi' ? 'महीना' : 'Month'}</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {pending.map((f, i) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 text-sm font-medium text-gray-800">{f.student_name || f.student_id}</td>
                    <td className="px-4 py-2.5 text-sm text-gray-600">{f.route_name || '-'}</td>
                    <td className="px-4 py-2.5 text-center text-sm font-bold text-orange-600">{f.amount ? `₹${f.amount}` : '-'}</td>
                    <td className="px-4 py-2.5 text-center text-sm text-gray-500">{f.month || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="text-center py-16 text-gray-400 bg-white rounded-xl border">
          <div className="text-5xl mb-3">💰</div>
          <p className="text-lg">{language === 'hi' ? 'कोई लंबित फीस नहीं' : 'No pending fees'}</p>
          <p className="text-sm mt-1">{language === 'hi' ? 'सभी ट्रांसपोर्ट फीस भर दी गई हैं' : 'All transport fees are cleared'}</p>
        </div>
      )}
    </div>
  );
}

// ==================== MAIN PAGE ====================
export default function TransportFullPage({ token }) {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('dashboard');

  const tabs = [
    { id: 'dashboard', label: language === 'hi' ? 'डैशबोर्ड' : 'Dashboard', icon: '📊' },
    { id: 'vehicles', label: language === 'hi' ? 'वाहन' : 'Vehicles', icon: '🚌' },
    { id: 'routes', label: language === 'hi' ? 'रूट' : 'Routes', icon: '🗺️' },
    { id: 'drivers', label: language === 'hi' ? 'ड्राइवर' : 'Drivers', icon: '👨‍✈️' },
    { id: 'students', label: language === 'hi' ? 'छात्र Mapping' : 'Student Mapping', icon: '👨‍🎓' },
    { id: 'fees', label: language === 'hi' ? 'फीस' : 'Fees', icon: '💰' },
  ];

  return (
    <div className="space-y-4" data-testid="transport-page">
      <div>
        <h1 className="text-xl font-bold text-gray-900">
          {language === 'hi' ? 'ट्रांसपोर्ट प्रबंधन' : 'Transport Management'}
        </h1>
        <p className="text-sm text-gray-500">
          {language === 'hi' ? 'वाहन, रूट, ड्राइवर और छात्र ट्रांसपोर्ट' : 'Vehicles, routes, drivers & student transport'}
        </p>
      </div>

      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit flex-wrap" data-testid="transport-tabs">
        {tabs.map(tab => (
          <button key={tab.id} data-testid={`tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 rounded-md text-sm font-medium transition ${activeTab === tab.id ? 'bg-white text-orange-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'}`}>
            <span className="mr-1">{tab.icon}</span>{tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'dashboard' && <DashboardTab token={token} language={language} />}
      {activeTab === 'vehicles' && <VehiclesTab token={token} language={language} />}
      {activeTab === 'routes' && <RoutesTab token={token} language={language} />}
      {activeTab === 'drivers' && <DriversTab token={token} language={language} />}
      {activeTab === 'students' && <StudentMappingTab token={token} language={language} />}
      {activeTab === 'fees' && <FeesTab token={token} language={language} />}
    </div>
  );
}
